// Block ids while a person edits (screen only).
//
// Three jobs on every document change:
//   1. assign  - top-level blocks with a null id get `generateBlockId()`;
//   2. normalize - ids below the top level are set back to null;
//   3. dedupe  - when two top-level blocks share an id, the first in document
//                order keeps it and the others get new ones.
//
// Splitting a block (Enter) keeps the id on the first half as a side effect of
// 3: both halves come out with the same id and the second is renumbered.
//
// The server is the source of ids, so transactions from y-sync (remote changes,
// the initial load) are not assigned ids. Normalizing and deduping still run on
// them: duplicates mostly come from merges (two people moving blocks at once, an
// agent write racing a move), which arrive through y-sync, and nobody else would
// fix them here. Fixes made while a remote change is applied are written back to
// Yjs on the next local edit; the server fixes duplicates on save as well.
//
// The assigning transaction does not set `addToHistory: false`: y-sync takes
// that flag from the last transaction of an update, so it would drop the user's
// own edit from the undo stack too.

import { Extension } from "@tiptap/core";
import { Fragment, type Node as PMNode, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
// The y-sync key must come from `@tiptap/y-tiptap` (the binding the screen's
// Collaboration extension uses). A key from another binding is another
// instance, and its meta is never set.
import { ySyncPluginKey } from "@tiptap/y-tiptap";
import { BLOCK_ID_ATTR, generateBlockId } from "./block-id";

export const blockIdPluginKey = new PluginKey("trameriaBlockId");

/**
 * Whether a transaction comes from y-sync (remote change, initial load,
 * undo/redo). Same test as Collaboration's `isChangeOrigin`.
 *
 * A transaction appended by a plugin is followed back to its root: ProseMirror
 * passes each plugin only the transactions it has not seen, so a plugin that
 * sees only another plugin's correction would miss that the root was remote.
 */
export function isYSyncTransaction(tr: Transaction): boolean {
  if (tr.getMeta(ySyncPluginKey)) return true;
  const root = tr.getMeta("appendedTransaction") as Transaction | undefined;
  return root !== undefined && !!root.getMeta(ySyncPluginKey);
}

function hasBlockIdAttr(node: PMNode): boolean {
  return BLOCK_ID_ATTR in node.attrs;
}

function blockIdOf(node: PMNode): string | null {
  const id = node.attrs[BLOCK_ID_ATTR];
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * The ids at depth 0. Nested ids are ignored on purpose: they exist only until
 * the next append transaction nulls them, and counting them would only make the
 * paste heuristic below drop ids for no reason.
 */
function collectTopLevelIds(doc: PMNode): Set<string> {
  const ids = new Set<string>();
  doc.forEach((child) => {
    const id = blockIdOf(child);
    if (id !== null) ids.add(id);
  });
  return ids;
}

/**
 * The append transaction, or undefined when there is nothing to fix
 * (ProseMirror calls again with the returned transaction; the second round
 * must return undefined). Walks the whole document every time: duplicates can
 * only be found that way.
 */
function fixUpBlockIds(
  tr: Transaction,
  doc: PMNode,
  { assignMissing }: { assignMissing: boolean },
): Transaction | undefined {
  const seen = new Set<string>();
  let changed = false;

  // setNodeMarkup keeps sizes, so positions counted on `doc` stay valid.
  let pos = 0;
  doc.forEach((child) => {
    const childPos = pos;
    pos += child.nodeSize;
    if (!hasBlockIdAttr(child)) return;

    const id = blockIdOf(child);
    if (id === null) {
      if (!assignMissing) return;
      const next = generateBlockId();
      seen.add(next);
      tr.setNodeMarkup(childPos, undefined, {
        ...child.attrs,
        [BLOCK_ID_ATTR]: next,
      });
      changed = true;
      return;
    }

    if (!seen.has(id)) {
      seen.add(id);
      return;
    }
    const next = generateBlockId();
    seen.add(next);
    tr.setNodeMarkup(childPos, undefined, {
      ...child.attrs,
      [BLOCK_ID_ATTR]: next,
    });
    changed = true;
  });

  doc.descendants((node, nodePos, parent) => {
    if (parent === doc) return true;
    if (blockIdOf(node) === null) return true;
    tr.setNodeMarkup(nodePos, undefined, {
      ...node.attrs,
      [BLOCK_ID_ATTR]: null,
    });
    changed = true;
    return true;
  });

  return changed ? tr : undefined;
}

/**
 * Drops block ids from a pasted slice, except an id that is not in the current
 * document (cut and paste: the block keeps its id and the conversations on it).
 * A copy pasted in the same document always loses its ids (the original is
 * still there); pasting the same clipboard twice keeps the id only the first
 * time. Ids below the top of the slice are always dropped. Returns the same
 * slice when nothing changes. Not for drag moves (see `transformPasted`).
 */
export function stripPastedBlockIds(slice: Slice, doc: PMNode): Slice {
  const existing = collectTopLevelIds(doc);
  const kept = new Set<string>();
  let changed = false;

  function walk(fragment: Fragment, depth: number): Fragment {
    const children: PMNode[] = [];
    fragment.forEach((node) => {
      const content =
        node.content.size > 0 ? walk(node.content, depth + 1) : node.content;
      const id = blockIdOf(node);
      const keep =
        id !== null && depth === 0 && !existing.has(id) && !kept.has(id);
      if (id !== null && keep) kept.add(id);
      if (id !== null && !keep) {
        children.push(
          node.type.create(
            { ...node.attrs, [BLOCK_ID_ATTR]: null },
            content,
            node.marks,
          ),
        );
        changed = true;
        return;
      }
      children.push(node.copy(content));
    });
    return Fragment.fromArray(children);
  }

  const content = walk(slice.content, 0);
  return changed ? new Slice(content, slice.openStart, slice.openEnd) : slice;
}

/** The ProseMirror plugin (usable headless; the editor gets {@link BlockIdPlugin}). */
export function createBlockIdPlugin(): Plugin {
  return new Plugin({
    key: blockIdPluginKey,

    appendTransaction(transactions, _oldState, newState) {
      if (!transactions.some((tr) => tr.docChanged)) return undefined;
      const fromYSync = transactions.some(isYSyncTransaction);
      return fixUpBlockIds(newState.tr, newState.doc, {
        assignMissing: !fromYSync,
      });
    },

    props: {
      transformPasted(slice, view) {
        // prosemirror-view also passes drags inside the editor through
        // transformPasted, before the source is removed. A move (dragging.move)
        // removes the source in the same transaction, so its ids are kept; a
        // copy drag is handled like a paste. A drag implementation must keep
        // `dragging.move` in line with whether the source is really removed
        // (the screen does it with `dragCopies`), or one id would exist twice.
        if (view.dragging?.move) return slice;
        return stripPastedBlockIds(slice, view.state.doc);
      },
    },
  });
}

/** {@link createBlockIdPlugin} as a TipTap extension (named apart from `blockId`). */
export const BlockIdPlugin = Extension.create({
  name: "blockIdPlugin",

  addProseMirrorPlugins() {
    return [createBlockIdPlugin()];
  },
});
